// ==========================================
// NEW SEARCH ARCHITECTURE - Phase 2
// router.js
//
// Pure decision logic for where a search should send the user
// (a direct collection, a specific product, or the default /search
// results page). This module does NOT navigate anywhere itself, and
// does not fetch anything - it only computes a destination from data
// the caller hands it.
//
// Phase 2 change: collection resolution no longer uses a hardcoded
// query -> URL dictionary. It looks up the brand SearchEngine
// detected against `collectionIndex`, the real, generated list of the
// store's actual collections (assets/collections-index.json, produced
// by scripts/generate-collection-index.js from the live storefront).
// If collectionIndex has no matching entry, this NEVER guesses a URL -
// it falls through to the default /search results page, which is the
// documented safe behavior (no 404 collection pages, ever).
// ==========================================

(function (global) {
  "use strict";

  var NewSearch = (global.NewSearch = global.NewSearch || {});

  if (NewSearch.Router) {
    return;
  }

  /**
   * Router
   * options:
   *   - collectionIndex: array of { title, handle, url, id } - the
   *     store's real collections, as generated by
   *     scripts/generate-collection-index.js. Never hand-authored.
   *   - defaultSearchPath: base path for the fallback search page (default "/search")
   */
  function Router(options) {
    options = options || {};
    this.collectionIndex = options.collectionIndex || [];
    this.defaultSearchPath = options.defaultSearchPath || "/search";
  }

  /**
   * Looks for a real collection whose title or handle mentions the
   * given canonical brand name. Returns the matching collection entry
   * or null - never constructs a URL that isn't already present in
   * collectionIndex.
   */
  Router.prototype._findCollectionForBrand = function (brand) {
    if (!brand) return null;
    var normalizer = NewSearch.normalizer;
    var normalizedBrand = normalizer ? normalizer.normalize(brand) : String(brand).toLowerCase();
    var norm = function (text) {
      return normalizer ? normalizer.normalize(text || "") : String(text || "").toLowerCase();
    };

    // Pass 1: exact match on title or handle alone. This is what
    // disambiguates near-duplicate brands like "boult" vs "go boult" -
    // "boult" is a genuine whole word inside "go boult", so a plain
    // substring scan (pass 2) can't tell them apart on its own.
    for (var i = 0; i < this.collectionIndex.length; i++) {
      var entry = this.collectionIndex[i];
      if (norm(entry.title) === normalizedBrand || norm(entry.handle) === normalizedBrand) {
        return entry;
      }
    }

    // Pass 2: fallback substring match, for collections whose
    // title/handle contains the brand plus other words (e.g. brand
    // "realme" against handle "realme-single-parts").
    for (var j = 0; j < this.collectionIndex.length; j++) {
      var candidate = this.collectionIndex[j];
      var haystack = norm((candidate.title || "") + " " + (candidate.handle || ""));
      if (haystack.indexOf(normalizedBrand) !== -1) {
        return candidate;
      }
    }
    return null;
  };

  /**
   * Given a normalized query and a SearchEngine result, returns a
   * destination descriptor:
   *   { type: "collection" | "product" | "fallback", url: string }
   *
   * Router rules (see project spec):
   *   1. Brand-only query with a real matching collection -> collection
   *   2. Confident single-product match -> product
   *   3/4. Everything else (ambiguous, no match, or a brand with no
   *        resolvable collection) -> fallback to the default Shopify
   *        search results page, which already renders "no products
   *        found" natively. This is also the safety net: it is
   *        impossible for this router to produce a 404 collection URL.
   *
   * Does not navigate - caller decides what to do with the result.
   */
  Router.prototype.resolve = function (normalizedQuery, engineResult) {
    if (engineResult && engineResult.isBrandOnlyQuery) {
      var collection = this._findCollectionForBrand(engineResult.brand);
      if (collection && collection.url) {
        return { type: "collection", url: collection.url };
      }
      // Brand recognized but no real collection resolves to it yet
      // (e.g. collections-index.json hasn't been regenerated against
      // the live store). A brand-only query is not specific enough to
      // pick one product either, so this goes straight to the search
      // results fallback below rather than falling through to the
      // product-match branch.
      var brandOnlyRawQuery = (engineResult && engineResult.query) || normalizedQuery || "";
      return {
        type: "fallback",
        url: this.defaultSearchPath + "?q=" + encodeURIComponent(brandOnlyRawQuery),
      };
    }

    if (engineResult && engineResult.confident && engineResult.best && engineResult.best.item) {
      var url = engineResult.best.item.url;
      if (url) {
        return { type: "product", url: url };
      }
    }

    var rawQuery = (engineResult && engineResult.query) || normalizedQuery || "";
    return {
      type: "fallback",
      url: this.defaultSearchPath + "?q=" + encodeURIComponent(rawQuery),
    };
  };

  NewSearch.Router = Router;
})(window);
